import { useCallback, useEffect } from 'react';
import { ActionPerformed, PushNotifications } from '@capacitor/push-notifications';
import { captureException, addBreadcrumb } from '@sentry/react';
import { v4 as uuid } from 'uuid';

import { getDevicePlatform } from '@ocx/utils-device';
import { useConfiguration } from '@ocx/data-configuration';
import { appendStoredPushNotificationEvent } from '@ocx/data-push-notifications';
import { useRudderStack } from '../../lib/rudderStack/useRudderStack';
import { PUSH_NOTIFICATION_ACTION_TAP, PUSH_NOTIFICATION_ACTION_DISMISS } from '../deep-linking/deep-linking.constants';
import { useDeepLinkNavigate } from '../deep-linking/deep-linking.utils';
import { getPushNotificationDataSchema } from './push-notification.schema';

export const usePushNotificationReceivedListener = () => {
  const devicePlatform = getDevicePlatform();

  const navigate = useDeepLinkNavigate();
  const { triggerEvent } = useRudderStack();
  const universalLinkOrigins = useConfiguration('localRetailerConfig.universalLinkOrigins');
  const PushNotificationDataSchema = getPushNotificationDataSchema(universalLinkOrigins);

  const onPushNotificationActionPerformed = useCallback(
    async (action: ActionPerformed) => {
      const now = Date.now();

      if (action.actionId === PUSH_NOTIFICATION_ACTION_DISMISS) {
        try {
          const data = await PushNotificationDataSchema.parseAsync(action.notification.data);
          appendStoredPushNotificationEvent({
            id: uuid(),
            event_type: 'notification_dismissed',
            push_id: data.push_id ?? null,
            occurred_at: now,
          }).catch((e) => captureException(e, { tags: { source: 'onPushNotificationActionPerformed' } }));
        } catch (e) {
          captureException(e, { tags: { source: 'onPushNotificationActionPerformed' } });
        }
        return;
      }

      if (action.actionId !== PUSH_NOTIFICATION_ACTION_TAP) {
        return;
      }

      try {
        const data = await PushNotificationDataSchema.parseAsync(action.notification.data);
        const pushId = data.push_id ?? null;

        if (data.url) {
          appendStoredPushNotificationEvent({
            id: uuid(),
            event_type: 'notification_link_opened',
            push_id: pushId,
            occurred_at: now,
          }).catch((e) => captureException(e, { tags: { source: 'onPushNotificationActionPerformed' } }));
          navigate(data.url);
        } else {
          appendStoredPushNotificationEvent({
            id: uuid(),
            event_type: 'notification_tapped',
            push_id: pushId,
            occurred_at: now,
          }).catch((e) => captureException(e, { tags: { source: 'onPushNotificationActionPerformed' } }));
        }
      } catch (e) {
        // Error is tracked here, because yup skips validation if the property is not provided
        triggerEvent('push_notification_link_error');
        captureException('Push notification link error', {
          data: { url: JSON.stringify(action.notification.data) },
        });
      }
    },
    [navigate, triggerEvent, PushNotificationDataSchema],
  );

  useEffect(() => {
    if (devicePlatform === 'web') {
      addBreadcrumb({
        message: '[usePushNotificationReceivedListener] skipping pushNotificationActionPerformed on web',
      });
      return () => {};
    }
    const subscription = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      onPushNotificationActionPerformed,
    );
    return () => {
      subscription.then((s) => s.remove());
    };
  }, [devicePlatform, onPushNotificationActionPerformed]);

  useEffect(() => {
    if (devicePlatform === 'web') {
      addBreadcrumb({
        message: '[usePushNotificationReceivedListener] skipping pushNotificationReceived on web',
      });
      return () => {};
    }
    const subscription = PushNotifications.addListener('pushNotificationReceived', () =>
      triggerEvent('push_notification_received'),
    );
    return () => {
      subscription.then((s) => s.remove());
    };
  }, [devicePlatform, triggerEvent]);
};
